import { computed, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useStorage } from "@vueuse/core";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import { httpRequest } from "@follaw-fr/utils/request.utils";
import { handleNetworkError } from "@follaw-fr/utils/handleNetworkError";
import type {
  LoginPayload,
  LogoutResponse,
  UserAuthenticatorAccessTokenPayload,
  UserAuthenticatorAccessTokenResponse,
} from "@commons/types/api/api.types";
import { isHTTPError } from "@follaw-fr/utils/is-http-error.utils";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import { useUserSettingsStore } from "./user-settings";
import { useUserStore } from "./user";
import { useRouteQuery } from "@vueuse/router";
import { redirectionApresConnexion } from "../utils/follaw-depuis-mail";

const toast = useToast();

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const urlRedirect = useRouteQuery("redirect");
  const userID = useStorage("userID", "");
  const token = useStorage<string | null>(
    window._fsv_config.ACCESS_TOKEN_KEY,
    null
  );

  const isTokenValid = computed(() => {
    if (token.value) {
      const tokenExpiryTimestamp =
        jwtDecode<{ exp: number }>(token.value).exp * 1000;
      return dayjs().isBefore(dayjs(tokenExpiryTimestamp));
    }
    return false;
  });

  watch(token, (newToken, oldToken) => {
    if (oldToken && !newToken) {
      return router.push({ name: "Connexion" });
    }
  });

  const reset = () => {
    const { reset: resetUser } = useUserStore();
    const { user } = storeToRefs(useUserSettingsStore());
    token.value = null;
    userID.value = "";
    user.value = null;
    resetUser();
  };

  const loginRequest = async (data: LoginPayload) => {
    try {
      const response = await httpRequest<
        UserAuthenticatorAccessTokenResponse,
        LoginPayload
      >("post", "/login", data);

      if (response.status === 202) {
        return {
          data: response.data,
          statusCode: response.status,
        };
      }

      // Redirect the user to the home view
      router.push("/");
    } catch (error: unknown) {
      if (isHTTPError(error)) {
        if (
          error.response.status === 404 &&
          error.response.dataMsg.match(/User not found/i)
        ) {
          toast.error("Pas de compte associé à cet email.");
        } else {
          handleNetworkError(error);
        }
      } else {
        handleNetworkError(error);
      }
    }
  };

  const createTokenWithDigicode = async (
    payload: UserAuthenticatorAccessTokenPayload
  ) => {
    try {
      const response = await httpRequest<
        UserAuthenticatorAccessTokenResponse,
        LoginPayload
      >("patch", "/login", payload);
      if (response.status === 200) {
        const accessToken = "Bearer " + response.data.access_token;
        token.value = accessToken;
        userID.value = response.data.user_id;
      }
      // Redirect the user to the home view
      if (urlRedirect.value) {
        return router.push(
          redirectionApresConnexion(urlRedirect.value as string)
        );
      }

      router.push("/");
    } catch (error: unknown) {
      if (isHTTPError(error)) {
        if (error.response.status >= 400) {
          if (error.response.status === 422) {
            toast.error(
              "Le lien que vous avez suivi n'est pas valide. Veuiller réessayer."
            );
          } else {
            toast.error(error.response.data?.detail || "Erreur inconnue");
            return {
              error: error.response.data?.detail || "Erreur inconnue",
            };
          }
        } else {
          return { error: error.response };
        }
      } else {
        handleNetworkError(error);
      }
    }
  };

  const logoutRequest = async () => {
    const response = await httpRequest<LogoutResponse, never>(
      "post",
      "/logout"
    );
    if (response.status === 200) {
      reset();

      try {
        window.CRISP_TOKEN_ID = null; // 1. Clear the crisp token value
        window.$crisp.push(["do", "session:reset"]); // 2. Unbind the current crisp session
        window.$crisp.push(["do", "chat:hide"]); // 3. Hide the chatbox
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  return {
    userID,
    token,
    isTokenValid,
    loginRequest,
    createTokenWithDigicode,
    logoutRequest,
    reset,
  };
});
